/**
 * Typed client for /admin-services/:id
 *
 * Each vendor endpoint returns `{ success: true, data: <VendorPayload> }`.
 * The controller (app/controllers/admin-services.js) calls
 * `utils.handleSuccess(res, 200, data)` which serialises to that shape,
 * so we use `unwrap<T>()` to peel the envelope.
 *
 * A 404 means the integration is NOT_CONFIGURED on the server — the UI
 * should render a "not connected" tile rather than an error banner.
 * Any other non-404 error is surfaced as a normal thrown AxiosError.
 */
import { AxiosError } from 'axios';
import { client, unwrap } from './client';

// ─── Vendor ids ───────────────────────────────────────────────────────────
// Must match the ADAPTERS map in app/controllers/admin-services.js exactly.
export type VendorId =
  | 'brevo'
  | 'twilio'
  | 'sentry'
  | 'better-stack'
  | 'posthog'
  | 'cloudflare'
  | 'quo';

// ─── Per-vendor payload shapes ────────────────────────────────────────────
// Derived from the per-vendor panel consumption in the source
// (src/views/admin/Services/index.jsx).

export interface BrevoData {
  sent24h?: number;
  bounce24h?: number;
  spamRate?: number;
  queueLength?: number;
  dailySent?: number[];
  events?: ServiceEvent[];
}

export interface TwilioData {
  sent24h?: number;
  failed24h?: number;
  cost24h?: number;
  dailySent?: number[];
  a2pStatus?: string;
  events?: ServiceEvent[];
}

export interface SentryData {
  errors24h?: number;
  newIssues24h?: number;
  releaseHealth?: number;
  crashFreeRate?: number;
  dailyErrors?: number[];
  events?: ServiceEvent[];
}

export interface BetterStackMonitor {
  id?: string;
  name?: string;
  url?: string;
  status?: string;
}

export interface BetterStackData {
  monitors?: BetterStackMonitor[];
  incidents24h?: number;
  sla30d?: number;
  mttr?: string;
}

export interface PostHogData {
  dau?: number;
  wau?: number;
  sessions24h?: number;
  conversion?: number;
  dailyDau?: number[];
  topEvents?: ServiceEvent[];
}

export interface CloudflareData {
  challenges24h?: number;
  blocked24h?: number;
  passed24h?: number;
  topRegion?: string;
  events?: ServiceEvent[];
}

export interface ServiceEvent {
  id?: string;
  message?: string;
  timestamp?: string;
  level?: string;
  [key: string]: unknown;
}

// Quo (formerly OpenPhone) — NUREV's business phone line.
export interface QuoNumber {
  number?: string;
  name?: string;
  status?: string;
  createdAt?: string;
}

export interface QuoData {
  numberCount?: number;
  memberCount?: number;
  messagingOk?: boolean;
  callingOk?: boolean;
  numbers?: QuoNumber[];
  events?: ServiceEvent[];
}

export type VendorData =
  | BrevoData
  | TwilioData
  | SentryData
  | BetterStackData
  | PostHogData
  | CloudflareData
  | QuoData;

// ─── Fetch helper ─────────────────────────────────────────────────────────

/**
 * Fetch live stats for a single vendor.
 *
 * Returns `null` when the endpoint responds with 404 (integration not
 * configured). Any other error is re-thrown so react-query surfaces it.
 */
export async function fetchServiceStatus(id: VendorId): Promise<VendorData | null> {
  try {
    return await unwrap<VendorData>(
      client.get<{ data: VendorData }>(`/admin-services/${id}`)
    );
  } catch (err) {
    const axErr = err as AxiosError;
    if (axErr?.response?.status === 404) {
      // NOT_CONFIGURED — render "not connected" tile, not an error
      return null;
    }
    throw err;
  }
}
